import React, { PropsWithChildren } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";

import {
    WCL_GRID_MAX_WIDTH,
    WCL_GRID_PADDING_LG,
    WCL_GRID_PADDING_SM
} from "@/libraries/design/foundations/grid/src/base";

import { BgColors } from "../../../components/link/src/link";

import { DraggableTab, DraggableTabList, TabPanel, Tabs } from "./";
export type TabDataType = {
    id: string;
    title: string;
    iconUrl?: string;
    tabComponent: React.ReactElement;
};
export type TabsSectionProps = PropsWithChildren<{
    readonly alignment?: "start" | "center";
    readonly tabsData: TabDataType[];
    readonly tabSize?: "small" | "large";
    readonly bgColor?: BgColors;
    readonly enableRovoBorder?: boolean;
    readonly enableResponsiveTabSize?: boolean;
}>;

export const TabsSection = ({
    alignment = "start",
    tabsData,
    tabSize = "large",
    bgColor = "bgWhite",
    enableRovoBorder = false,
    enableResponsiveTabSize = false
}: TabsSectionProps) => {
    const sectionStyles = css({
        width: "100%",
        overflow: "hidden"
    });
    const fullWidthContainerStyles = css({
        margin: "auto",
        maxWidth: "100%",
        boxSizing: "border-box",
        overflow: "visible",
        paddingInlineStart: `${WCL_GRID_PADDING_SM}px`,
        paddingInlineEnd: `${WCL_GRID_PADDING_SM}px`,
        width: "100%",
        [media.only.sm]: {
            paddingInlineStart: `${WCL_GRID_PADDING_LG}px`,
            paddingInlineEnd: `${WCL_GRID_PADDING_LG}px`
        },
        [media.above.md]: {
            paddingInlineStart: `${WCL_GRID_PADDING_LG}px`,
            paddingInlineEnd: `${WCL_GRID_PADDING_LG}px`,
            maxWidth: `${WCL_GRID_MAX_WIDTH + WCL_GRID_PADDING_LG * 2}px`
        }
    });

    const enableIconTabs = tabsData.every((tab) => tab.iconUrl);

    return (
        <section id="tabs" css={sectionStyles}>
            <Tabs tabIds={tabsData.map((tab) => tab.id)}>
                <DraggableTabList tabSize={tabSize} alignment={alignment}>
                    {tabsData.map((tab, index) => (
                        <DraggableTab
                            key={index}
                            tabIndex={index}
                            title={tab.title}
                            iconUrl={enableIconTabs ? tab.iconUrl : undefined}
                            tabSize={tabSize}
                            bgColor={bgColor}
                            className="round-tab"
                            enableRovoBorder={
                                enableIconTabs && enableRovoBorder
                            }
                            enableResponsiveTabSize={
                                enableIconTabs && enableResponsiveTabSize
                            }
                        />
                    ))}
                </DraggableTabList>
                <div
                    css={tabSize === "large" && fullWidthContainerStyles}
                    className="tab-panels"
                >
                    {tabsData.map((tab, index) => (
                        <TabPanel
                            panelIndex={index}
                            id={tab.id || `tab-${index}`}
                            key={index}
                        >
                            {tab.tabComponent}
                        </TabPanel>
                    ))}
                </div>
            </Tabs>
        </section>
    );
};
